import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { UserIcon } from '@heroicons/react/24/outline';
import Button from '../../components/ui/Button';
import FormInput from '../../components/ui/FormInput';
import PasswordInput from '../../components/settings/components/PasswordInput';
import SocialLoginButtons from './components/SocialLoginButtons';
import { useLoginMutation } from '../../redux/apis/endpoints/authApi';
import authSlice from '../../redux/slices/authSlice';

const Login = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);
  const userLang = navigator.language || navigator.userLanguage;
  const [credentials, setCredentials] = useState({ identifier: '', password: '' });
  const [errors, setErrors] = useState({ identifier: '', password: '', general: '' });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Get the message from location state (e.g., after email verification)
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);

  // RTK Query hook for login
  const [login, { isLoading, error: loginError }] = useLoginMutation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle login error and show appropriate message
  useEffect(() => {
    if (loginError) {
      console.log('Login error received:', loginError);
      
      // Default error message
      let errorMessage = 'Unable to log in. Please check your credentials and try again.';
      let isUnconfirmedError = false;
      
      try {
        // Check for specific error types in the nested structure
        if (loginError.data?.message && Array.isArray(loginError.data.message)) {
          const messageObj = loginError.data.message[0]?.messages?.[0];
          if (messageObj?.id === "Auth.form.error.invalid") {
            errorMessage = 'Invalid credentials. Please check your email/username and password.';
          } else if (messageObj?.message) {
            errorMessage = messageObj.message;
          }
          
          // Check for email confirmation errors
          isUnconfirmedError = messageObj?.id?.includes('confirmed') || 
                              (messageObj?.message && (
                                messageObj.message.toLowerCase().includes('not confirmed') || 
                                messageObj.message.toLowerCase().includes('email confirmation') || 
                                messageObj.message.toLowerCase().includes('unconfirmed')
                              ));
        }
        // Check for specific error types in the data array
        else if (loginError.data?.data && Array.isArray(loginError.data.data)) {
          const messageObj = loginError.data.data[0]?.messages?.[0];
          if (messageObj?.id === "Auth.form.error.invalid") {
            errorMessage = 'Invalid credentials. Please check your email/username and password.';
          } else if (messageObj?.message) {
            errorMessage = messageObj.message;
          }
          
          // Check for email confirmation errors
          isUnconfirmedError = messageObj?.id?.includes('confirmed') || 
                              (messageObj?.message && (
                                messageObj.message.toLowerCase().includes('not confirmed') || 
                                messageObj.message.toLowerCase().includes('email confirmation') || 
                                messageObj.message.toLowerCase().includes('unconfirmed')
                              ));
        }
        // Check for error in the traditional error object format
        else if (loginError.data && loginError.data.error) {
          // Handle nested error object from Strapi
          errorMessage = 
            loginError.data.error.message || 
            loginError.data.message || 
            errorMessage;
          
          // Also check string content for confirmation errors
          isUnconfirmedError = errorMessage.toLowerCase().includes('not confirmed') || 
                          errorMessage.toLowerCase().includes('email confirmation') || 
                          errorMessage.toLowerCase().includes('unconfirmed');
        } else if (loginError.data) {
          // Handle direct message in data
          errorMessage = loginError.data.message || errorMessage;
          
          // Check for confirmation error
          isUnconfirmedError = 
            (errorMessage.toLowerCase().includes('not confirmed') || 
            errorMessage.toLowerCase().includes('email confirmation') || 
            errorMessage.toLowerCase().includes('unconfirmed'));
        }
      } catch (err) {
        console.error('Error parsing login error:', err);
      }
      
      if (isUnconfirmedError) {
        // Get the email from the credentials if it looks like an email
        let email = credentials.identifier;
        if (email && !email.includes('@')) {
          // If identifier is not an email, don't use it
          email = '';
        }
        
        // Redirect to check email page with appropriate message
        history.push('/auth/check-email', { 
          email,
          message: 'Your account email is not confirmed. Please check your inbox for the confirmation email or request a new one below.'
        });
      } else {
        // Show other errors normally
        setErrors(prev => ({ ...prev, general: errorMessage }));
      }
    }
  }, [loginError, credentials.identifier, history]);

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };
  
  const validateForm = () => {
    let isValid = true;
    const newErrors = { identifier: '', password: '', general: '' };
    
    // Only validate as email if it contains @ symbol
    if (credentials.identifier.includes('@') && !validateEmail(credentials.identifier)) {
      newErrors.identifier = 'Please enter a valid email address';
      isValid = false;
    }
    
    if (!credentials.identifier) {
      newErrors.identifier = 'Username or email is required';
      isValid = false;
    }
    
    if (!credentials.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear general error when any field changes
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate form first
    if (!validateForm()) {
      return;
    }
    
    setErrors({ identifier: '', password: '', general: '' });

    try {
      // Attempt to log in - this will throw an error if login fails
      const result = await login(credentials).unwrap();
      
      console.log('Login successful, received result:', result);
      
      // Validate the response contains what we need
      if (!result || !result.jwt) {
        throw new Error('Login response missing token');
      }
      
      // Extract user and token from response
      const user = result.user || {};
      const token = result.jwt;
      
      try {
        // Store authentication state in Redux 
        console.log('Dispatching credentials to Redux:', { user, token });
        
        // Try multiple ways to update Redux state
        try {
          dispatch(authSlice.actions.setCredentials({
            user,
            token
          }));
        } catch (dispatchError1) {
          console.error('First dispatch method failed:', dispatchError1);
          try {
            // Try direct action type
            dispatch({
              type: 'auth/setCredentials',
              payload: { user, token }
            });
          } catch (dispatchError2) {
            console.error('Second dispatch method failed:', dispatchError2);
          }
        }
      } catch (dispatchError) {
        // If Redux dispatch fails, log it but still continue with the login process
        console.error('Error dispatching to Redux:', dispatchError);
        
        // Manual fallback - store in localStorage directly
        localStorage.setItem('token', token);
        localStorage.setItem('userInfo', JSON.stringify(user));
        localStorage.setItem('login', 'true');
        localStorage.setItem('loginTime', Date.now().toString());
      }
      
      // Show success message
      setSuccessMessage('Login successful! Redirecting to dashboard...');
      
      // Redirect to dashboard or home page after successful login
      setTimeout(() => {
        // Check if Redux state was properly updated
        const authUpdated = localStorage.getItem('token') && 
                           localStorage.getItem('userInfo') && 
                           localStorage.getItem('login') === 'true';
        
        if (!authUpdated) {
          console.warn('Redux state may not have updated properly. Using localStorage fallback.');
          // Ensure localStorage has the session data as fallback
          localStorage.setItem('token', token);
          localStorage.setItem('userInfo', JSON.stringify(user));
          localStorage.setItem('login', 'true');
          localStorage.setItem('loginTime', Date.now().toString());
        }
        
        const redirectTo = '/dashboard';
        console.log('Redirecting to:', redirectTo);
        history.push(redirectTo);
      }, 1000);
    } catch (err) {
      // If login fails, the error is handled by the useEffect hook
      // No credentials are set in this case
      console.error('Login error:', err);
    }
  };

  // Check if this is a closet user from URL params
  const urlParams = new URLSearchParams(location.search);
  const isClosetUser = urlParams.get('isClosetUser');

  // Determine background style
  const backgroundStyle = isClosetUser
    ? {
        minHeight: '100vh',
        backgroundImage:
          "url('https://res.cloudinary.com/sheyou/image/upload/v1727875894/virtual_closet_5d66ccc54e.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {
        minHeight: '100vh'
      };

  // Common input style for consistency across all inputs
  const inputStyle = "text-gray-900 placeholder:text-gray-400";

  return (
    <div className="gradient_limeade flex flex-col" style={backgroundStyle}>
      <div className="flex flex-grow items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-md">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Sign in to your account
            </h2>
          </div>
          
          {errors.general && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{errors.general}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {successMessage && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Success</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>{successMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
              <FormInput
                label="Username or Email"
                id="identifier"
                name="identifier"
                type="text"
                autoComplete="username email"
                required
                value={credentials.identifier}
                onChange={handleChange}
                placeholder="Enter your username or email"
                leftIcon={<UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                className={inputStyle}
                disabled={isLoading}
                error={errors.identifier}
                fluid={true}
              />
              
              <PasswordInput
                label="Password"
                value={credentials.password}
                onChange={(e) => handleChange({ target: { name: 'password', value: e.target.value } })}
                name="password"
                id="password"
                autoComplete="current-password"
                required
                placeholder="Password"
                userLang={userLang}
                className={inputStyle}
                disabled={isLoading}
                error={errors.password}
              />
            </div>

            <div className="flex items-center justify-between mt-6">
              <div className="text-sm">
                <Link
                  to="/auth/forgot-password"
                  className="font-medium text-brand-teal hover:text-brand-teal-dark"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div className="mt-6">
              <Button 
                type="submit"
                className="w-full" 
                variant="primary"
                disabled={isLoading}
                isLoading={isLoading}
              >
                Sign in
              </Button>
            </div>
          </form>

          {/* Social Login Buttons */}
          <SocialLoginButtons redirectTo="/dashboard" />

          <div className="text-center text-sm text-gray-500">
            Don't have an account?{' '}
            <Link 
              to="/register" 
              className={`font-medium text-brand-teal hover:text-brand-teal-light ${isLoading ? 'pointer-events-none opacity-50' : ''}`}
            >
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 